import {Code, Cloud, Smartphone, LineChart, Shield, Cog, ArrowRight, Sparkles} from 'lucide-react';

const services = [
    {
        icon: Code,
        title: 'Custom Software Development',
        description: 'Transform your vision into production-ready applications that scale. We build high-performance, maintainable systems using cutting-edge tech stacks.',
        outcomes: ['3-6 month delivery', '40% faster than industry average', 'Zero technical debt'],
        gradient: 'from-blue-600 to-cyan-500',
    },
    {
        icon: Cloud,
        title: 'Cloud Solutions & Migration',
        description: 'Migrate to the cloud with zero downtime and achieve 60% cost savings. Optimize infrastructure for performance, security, and scalability.',
        outcomes: ['99.99% uptime guaranteed', '60% cost reduction', 'Auto-scaling infrastructure'],
        gradient: 'from-purple-600 to-blue-600',
    },
    {
        icon: Smartphone,
        title: 'Mobile App Development',
        description: 'Launch stunning iOS and Android apps that users love. Drive engagement with intuitive UX and lightning-fast performance.',
        outcomes: ['Cross-platform or native', '4.8+ app store ratings', 'Millions of MAU supported'],
        gradient: 'from-cyan-500 to-blue-500',
    },
    {
        icon: LineChart,
        title: 'Digital Transformation',
        description: 'Eliminate legacy bottlenecks and unlock new revenue streams. Modernize your tech stack and outpace the competition.',
        outcomes: ['Legacy system modernization', '3x operational efficiency', 'New revenue channels'],
        gradient: 'from-indigo-600 to-purple-600',
    },
    {
        icon: Shield,
        title: 'Cybersecurity Consulting',
        description: 'Protect your business with enterprise-grade security. Comprehensive audits, penetration testing, and compliance certifications.',
        outcomes: ['SOC 2, HIPAA, GDPR ready', 'Zero-trust architecture', '24/7 threat monitoring'],
        gradient: 'from-red-600 to-orange-500',
    },
    {
        icon: Cog,
        title: 'DevOps & Automation',
        description: 'Ship code 10x faster with modern CI/CD pipelines. Automate everything from testing to deployment for maximum velocity.',
        outcomes: ['10x faster deployments', 'Automated testing & QA', '80% reduction in bugs'],
        gradient: 'from-emerald-600 to-teal-500',
    },
];

export function Services() {
    return (
        <section id="services" className="py-20 md:py-32 relative overflow-hidden bg-white">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-white"/>
            <div
                className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/5 to-transparent rounded-full blur-3xl"/>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <div className="text-center mb-20 max-w-4xl mx-auto animate-fadeInUp">
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                        <Sparkles size={16} className="text-blue-600"/>
                        <span className="text-sm text-blue-700 font-semibold uppercase tracking-wider">
              Our Expertise
            </span>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
                        <span className="gradient-text-premium">Solutions That Drive</span>
                        <br/>
                        <span className="text-gray-900">Real Business Impact</span>
                    </h2>
                    <p className="text-xl text-gray-600 leading-relaxed">
                        From strategy to execution, we deliver comprehensive technology services that transform your
                        business and create lasting competitive advantages.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {services.map((service, index) => {
                        const Icon = service.icon;
                        return (
                            <div
                                key={index}
                                className="group premium-card p-8 rounded-2xl hover-lift"
                                style={{animationDelay: `${index * 0.1}s`}}
                            >
                                {/* Icon with gradient */}
                                <div
                                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} text-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                    <Icon size={32}/>
                                </div>

                                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                                    {service.title}
                                </h3>

                                <p className="text-gray-600 leading-relaxed mb-6">
                                    {service.description}
                                </p>

                                {/* Outcomes/Benefits */}
                                <div className="space-y-2 mb-6 pb-6 border-b border-gray-100">
                                    {service.outcomes.map((outcome, idx) => (
                                        <div key={idx} className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"/>
                                            <span className="text-sm text-gray-700 font-medium">{outcome}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <button
                                    onClick={() => {
                                        const element = document.getElementById('contact');
                                        if (element) element.scrollIntoView({behavior: 'smooth'});
                                    }}
                                    className="flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all group-hover:text-blue-700"
                                >
                                    <span>Get Started</span>
                                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform"/>
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <div className="mt-20 text-center animate-fadeInUp">
                    <div style={{backgroundColor: "#5743ff"}}
                         className="inline-block p-8 md:p-12 rounded-3xl text-white shadow-premium-lg">
                        <h3 className="text-3xl md:text-4xl font-bold mb-4">
                            Not sure where to start?
                        </h3>
                        <p className="text-lg md:text-xl mb-8 opacity-95 max-w-2xl mx-auto">
                            Schedule a free consultation with our experts. We'll analyze your needs and recommend the
                            perfect solution.
                        </p>
                        <button
                            onClick={() => {
                                const element = document.getElementById('contact');
                                if (element) element.scrollIntoView({behavior: 'smooth'});
                            }}
                            className="px-10 py-5 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
                        >
                            Book Free Consultation
                            <ArrowRight size={22}/>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}